/* Attachment dropzone for BLANKD quote forms.

   Shopify's contact form cannot carry attachments, so files are uploaded to Vercel Blob
   first and their URLs are appended to the message. The merchant gets working links and
   there is still only one inbox.

   Progressive on purpose: with JS off, or if the studio host is unreachable, the form is a
   plain contact form that still submits. A quote must never be lost because an upload failed.

   Binds to any [data-bdx-drop] on the page, so a future pop-out quote panel gets this for
   free by rendering the same markup. */
(() => {
  const MB = 1024 * 1024;
  const MAX = 30 * MB;
  const kb = (n) => (n < MB ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / MB).toFixed(1)} MB`);

  document.querySelectorAll("[data-bdx-drop]").forEach((root) => {
    const form = root.closest("form");
    const input = root.querySelector("input[type=file]");
    const zone = root.querySelector("[data-drop-zone]");
    const list = root.querySelector("[data-drop-list]");
    const msg = root.querySelector("[data-drop-msg]");
    if (!form || !input || !zone || !list) return;

    const host = (root.dataset.host || "").replace(/\/$/, "");
    let picked = [];
    let busy = false;
    let failed = false;   // an upload already failed; the next submit goes without the files

    const say = (text, state) => {
      if (!msg) return;
      msg.textContent = text || "";
      if (state) msg.setAttribute("data-state", state); else msg.removeAttribute("data-state");
    };

    const paint = () => {
      list.innerHTML = picked
        .map((f, i) => `<div><span>${f.name.replace(/[<>&]/g, "")}</span><em>${kb(f.size)}<button type="button" data-rm="${i}" aria-label="Remove ${f.name.replace(/["<>&]/g, "")}">&#10005;</button></em></div>`)
        .join("");
    };

    const accept = (files) => {
      const tooBig = [...files].filter((f) => f.size > MAX);
      picked = picked.concat([...files].filter((f) => f.size <= MAX)).slice(0, 8);
      failed = false;
      paint();
      say(tooBig.length ? `${tooBig[0].name} is over 30 MB — send a link instead.` : "", tooBig.length ? "error" : null);
    };

    zone.addEventListener("click", () => input.click());
    zone.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); input.click(); }
    });
    input.addEventListener("change", () => { accept(input.files); input.value = ""; });

    ["dragenter", "dragover"].forEach((t) =>
      zone.addEventListener(t, (e) => { e.preventDefault(); zone.classList.add("is-hot"); }));
    ["dragleave", "drop"].forEach((t) =>
      zone.addEventListener(t, (e) => { e.preventDefault(); zone.classList.remove("is-hot"); }));
    zone.addEventListener("drop", (e) => { if (e.dataTransfer?.files?.length) accept(e.dataTransfer.files); });

    list.addEventListener("click", (e) => {
      const i = e.target.closest("[data-rm]")?.dataset.rm;
      if (i == null) return;
      picked.splice(Number(i), 1);
      paint();
    });

    form.addEventListener("submit", async (e) => {
      if (!picked.length || busy) return;           // nothing attached: normal submit

      // Second press after a failure: send the quote anyway and name the files, so a broken
      // upload costs us a follow-up email rather than the whole enquiry.
      if (failed) {
        const body = form.querySelector("[name='contact[body]']");
        if (body) body.value = `${body.value}\n\nCould not attach (ask by reply):\n${picked.map((f) => `- ${f.name}`).join("\n")}`;
        return;
      }

      e.preventDefault();
      busy = true;

      // Disable rather than relabel: the button holds an arrow element that a textContent
      // swap would delete. Progress goes on the message line instead.
      const submit = form.querySelector("[type=submit]");
      if (submit) submit.disabled = true;

      try {
        // 100 KB of Blob client — loaded only once someone actually attaches something.
        const { uploadQuoteFiles } = await import(`${host}/piobox-customizer/quote-upload.js`);
        const done = await uploadQuoteFiles(picked, {
          handleUploadUrl: `${host}/api/upload`,
          onProgress: (n, total, name) => say(`Uploading ${n} of ${total} — ${name}`),
        });

        const body = form.querySelector("[name='contact[body]']");
        if (body) {
          const lines = done.map((f) => `- ${f.name} (${kb(f.size)}): ${f.url}`).join("\n");
          body.value = `${body.value}\n\nAttached:\n${lines}`;
        }
        picked = [];
        busy = false;
        form.submit();                              // native submit, listener already spent
      } catch (err) {
        busy = false;
        failed = true;
        if (submit) submit.disabled = false;
        say(`Could not attach those files (${err?.message || "upload failed"}). Press send again and we'll ask for them by reply.`, "error");
      }
    });
  });
})();
